<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const { t } = useI18n();

const chartData = computed(() => ({
  labels: props.data.map(item => item.date),
  datasets: [
    {
      label: t('CAPTAIN_ANALYTICS.CHART.CONVERSATIONS'),
      data: props.data.map(item => item.conversations),
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      tension: 0.3,
    },
    {
      label: t('CAPTAIN_ANALYTICS.CHART.POSITIVE'),
      data: props.data.map(item => item.positive),
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      tension: 0.3,
    },
    {
      label: t('CAPTAIN_ANALYTICS.CHART.NEGATIVE'),
      data: props.data.map(item => item.negative),
      borderColor: 'rgb(239, 68, 68)',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      tension: 0.3,
    },
    {
      label: t('CAPTAIN_ANALYTICS.CHART.LEADS'),
      data: props.data.map(item => item.leads),
      borderColor: 'rgb(168, 85, 247)',
      backgroundColor: 'rgba(168, 85, 247, 0.1)',
      tension: 0.3,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 20,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(148, 163, 184, 0.1)',
      },
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col p-4 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container"
  >
    <h3 class="text-base font-medium text-n-slate-12 mb-4">
      {{ $t('CAPTAIN_ANALYTICS.TRENDS_OVER_TIME') }}
    </h3>
    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <Spinner />
    </div>
    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-64 text-n-slate-11"
    >
      {{ $t('CAPTAIN_ANALYTICS.NO_DATA') }}
    </div>
    <div v-else class="h-64">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>
